import { AssetRef, Resource, ResourceReviewItem, Shot } from '../../../types';
import { ShotEditFormData } from '../types';
import ShotActionBar from './ShotActionBar';
import ShotAssetResourceGrid from './ShotAssetResourceGrid';
import ShotPromptPanel from './ShotPromptPanel';
import { useIsMobile } from '../../../utils/useIsMobile';
import { tosAccelerate } from '../../../utils/tosAccelerate';
import type { PreviewAsset } from './AssetPreviewLightbox';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotDetailsProps {
  shot: Shot;
  selectedVideoUrl: string;
  aspectRatio?: string;
  languageSkillId?: string;
  languageSkillName: string;
  applyingLanguage: boolean;
  rematching: boolean;
  generating: boolean;
  resourceReviews: ResourceReviewItem[];
  credits: number | null;
  refs: AssetRef[];
  resources: Resource[];
  uploadingResource: boolean;
  assetCategoryMap: Map<string, string>;
  projectId: string;
  t: Translate;
  onDelete: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onRematchAssets: () => void;
  onApplyLanguage: () => void;
  onGenerateVideo: () => void;
  onPreviewAsset: (asset: PreviewAsset) => void;
  onRemoveAssetRef: (imgIndex: number) => void;
  onDeleteResource: (resourceId: string) => void;
  onUploadResource: () => void;
  onPickAsset: () => void;
  onSwitchState: (ref: AssetRef) => void;
  onOpenComments: () => void;
  onPreviewPlayed?: () => void;
  editing: boolean;
  savingShot: boolean;
  shotEdits: ShotEditFormData;
  onShotEditsChange: React.Dispatch<React.SetStateAction<ShotEditFormData>>;
  onSave: () => void;
  onCancel: () => void;
  onSaveAndGenerate: () => void;
  minDuration?: number;
  maxDuration?: number;
}

export default function ShotDetails({
  shot,
  selectedVideoUrl,
  aspectRatio,
  languageSkillId,
  languageSkillName,
  applyingLanguage,
  rematching,
  generating,
  resourceReviews,
  credits,
  refs,
  resources,
  uploadingResource,
  assetCategoryMap,
  projectId,
  t,
  onDelete,
  onEdit,
  onDuplicate,
  onRematchAssets,
  onApplyLanguage,
  onGenerateVideo,
  onPreviewAsset,
  onRemoveAssetRef,
  onDeleteResource,
  onUploadResource,
  onPickAsset,
  onSwitchState,
  onOpenComments,
  onPreviewPlayed,
  editing,
  savingShot,
  shotEdits,
  onShotEditsChange,
  onSave,
  onCancel,
  onSaveAndGenerate,
  minDuration,
  maxDuration,
}: ShotDetailsProps) {
  const isMobile = useIsMobile();
  const isPortrait = aspectRatio === '9:16';
  const videoWidth = isPortrait ? '25%' : '40%';
  const videoMinWidth = isPortrait && !isMobile ? '400px' : 0;

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      {selectedVideoUrl && (
        <div style={{ flex: `0 0 ${videoWidth}`, minWidth: videoMinWidth }}>
          <video
            src={tosAccelerate(selectedVideoUrl)}
            controls
            onPlay={onPreviewPlayed}
            style={{ width: '100%', borderRadius: '8px', display: 'block' }}
          />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <ShotActionBar
          shot={shot}
          languageSkillId={languageSkillId}
          languageSkillName={languageSkillName}
          applyingLanguage={applyingLanguage}
          rematching={rematching}
          generating={generating}
          resourceReviews={resourceReviews}
          resources={resources}
          credits={credits}
          t={t}
          onDelete={onDelete}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onRematchAssets={onRematchAssets}
          onApplyLanguage={onApplyLanguage}
          onGenerateVideo={onGenerateVideo}
          pendingCommentCount={shot.pendingCommentCount ?? 0}
          latestPendingComment={shot.latestPendingComment}
          onOpenComments={onOpenComments}
          editing={editing}
          savingShot={savingShot}
          onSave={onSave}
          onCancel={onCancel}
          onSaveAndGenerate={onSaveAndGenerate}
        />

        <ShotPromptPanel
          shot={shot}
          hasLanguageSkill={Boolean(languageSkillId)}
          languageSkillName={languageSkillName}
          t={t}
          editing={editing}
          shotEdits={shotEdits}
          onShotEditsChange={onShotEditsChange}
          refs={refs}
          resources={resources}
          minDuration={minDuration}
          maxDuration={maxDuration}
        />

        <ShotAssetResourceGrid
          refs={refs}
          resources={resources}
          uploadingResource={uploadingResource}
          assetCategoryMap={assetCategoryMap}
          projectId={projectId}
          t={t}
          onPreviewAsset={onPreviewAsset}
          onRemoveAssetRef={onRemoveAssetRef}
          onDeleteResource={onDeleteResource}
          onUploadResource={onUploadResource}
          onPickAsset={onPickAsset}
          onSwitchState={onSwitchState}
        />
      </div>
    </div>
  );
}
